/**
 * Ten Mantine shades from one hex (`13` §6.5, `AM-173`): the shape `theme.ts`'s own `steel` ramp
 * uses, so a tenant's `primaryColor` can override it. Index 7 is the app's own convention - "the
 * design's reference hue" (`theme.ts`) - so the input color lands there, 0–6 tint it toward white
 * in even steps and 8–9 shade it toward black, all at the same hue and saturation. No external
 * color library: {@link ../color/color} carries the HSL math both this and the contrast helper need.
 */

import { hslToHex, parseHexColor, rgbToHsl } from './color';

export type MantineShades = readonly [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

/** The lightest tint (index 0): near-white, never white itself, so index 0 stays a distinct swatch. */
const LIGHTEST = 97;

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/** `null` when `hex` is not `#RRGGBB` - the caller decides whether to fall back to the design's own `steel`. */
export function buildMantineShades(hex: string): MantineShades | null {
  const rgb = parseHexColor(hex);
  if (rgb === null) {
    return null;
  }

  const { h, s, l } = rgbToHsl(rgb);
  // Seven tints (index 0–6) approach `l` without ever reaching it - `l` itself is index 7 below.
  const [t0, t1, t2, t3, t4, t5, t6] = Array.from({ length: 7 }, (_, index) =>
    hslToHex({ h, s, l: lerp(LIGHTEST, l, index / 7) }),
  ) as [string, string, string, string, string, string, string];
  const base = hslToHex({ h, s, l });
  const [shade8, shade9] = [0.72, 0.48].map((factor) => hslToHex({ h, s, l: l * factor })) as [
    string,
    string,
  ];

  return [t0, t1, t2, t3, t4, t5, t6, base, shade8, shade9];
}
